import type { components } from '@trackercop/contracts';
import {
  PAYLOAD_CATEGORY_SEVERITY,
  PAYLOAD_CATEGORY_EDGE_COLOR,
  SENSITIVE_CATEGORIES,
  dominantCategoryOf,
} from './payload-category-palette';

type PayloadRecord = components['schemas']['PayloadRecord'];
type PayloadCategory = components['schemas']['PayloadCategory'];
type ConsentMode = components['schemas']['ConsentMode'];

// Re-export the shared palette so existing call sites
// (`data-sharing-map.tsx` etc.) keep working unchanged. The canonical
// definitions live in `payload-category-palette.ts` so the per-crawl
// view and the site-level data-flow view share the same severity
// ladder + colours.
export {
  PAYLOAD_CATEGORY_SEVERITY,
  PAYLOAD_CATEGORY_EDGE_COLOR,
  SENSITIVE_CATEGORIES,
};

export interface DataSharingScriptNode {
  id: string; // `script:<scriptId>` or `script:<originDomain>` when scriptId is null
  scriptId: string | null;
  label: string;
  requestCount: number;
}

export interface DataSharingDestinationNode {
  id: string; // `dest:<domain>`
  domain: string;
  requestCount: number;
}

export interface DataSharingEdge {
  sourceId: string; // script node id
  targetId: string; // destination node id
  weight: number; // payload record count on this flow
  dominantCategory: PayloadCategory;
  categories: ReadonlySet<PayloadCategory>;
  consentModes: ReadonlySet<ConsentMode>;
}

export interface DataSharingGraph {
  scripts: DataSharingScriptNode[];
  destinations: DataSharingDestinationNode[];
  edges: DataSharingEdge[];
}

interface BuildFilters {
  consentMode?: ConsentMode | 'all';
  sensitiveOnly?: boolean;
}

const dominantOf = dominantCategoryOf;

function safeHostname(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

function scriptNodeIdFor(r: PayloadRecord): { id: string; label: string } {
  if (r.scriptId) {
    // Prefer a readable label; fall back to the full URL.
    const host = safeHostname(r.requestUrl) ?? r.requestUrl.slice(0, 64);
    return { id: `script:${r.scriptId}`, label: host };
  }
  // Direct (first-party) requests without an attributed script. Group
  // them under the request origin so the graph doesn't explode.
  const origin = safeHostname(r.requestUrl) ?? 'unknown';
  return { id: `script:${origin}`, label: `${origin} (direct)` };
}

// Build the bipartite graph from raw PayloadRecord rows. We work from
// the record stream — not the per-destination / per-script aggregates
// — because we need the per-edge category set, and the aggregate
// endpoints do not expose it.
export function buildDataSharingGraph(
  records: readonly PayloadRecord[],
  filters: BuildFilters = {},
): DataSharingGraph {
  const consent = filters.consentMode && filters.consentMode !== 'all' ? filters.consentMode : null;

  const scripts = new Map<string, DataSharingScriptNode & { scriptId: string | null }>();
  const destinations = new Map<string, DataSharingDestinationNode>();
  const edgeMap = new Map<
    string,
    {
      sourceId: string;
      targetId: string;
      weight: number;
      categories: Set<PayloadCategory>;
      consentModes: Set<ConsentMode>;
    }
  >();

  for (const r of records) {
    if (consent && r.consentMode !== consent) continue;

    const scriptNode = scriptNodeIdFor(r);
    const destId = `dest:${r.destinationDomain}`;

    const cats = r.payloadCategories && r.payloadCategories.length > 0
      ? r.payloadCategories
      : (['benign'] as PayloadCategory[]);

    const existingScript = scripts.get(scriptNode.id);
    if (!existingScript) {
      scripts.set(scriptNode.id, { id: scriptNode.id, scriptId: r.scriptId ?? null, label: scriptNode.label, requestCount: 1 });
    } else {
      existingScript.requestCount += 1;
    }
    const existingDest = destinations.get(destId);
    if (!existingDest) {
      destinations.set(destId, { id: destId, domain: r.destinationDomain, requestCount: 1 });
    } else {
      existingDest.requestCount += 1;
    }

    const edgeKey = `${scriptNode.id}\u0000${destId}`;
    const edge = edgeMap.get(edgeKey);
    if (!edge) {
      edgeMap.set(edgeKey, {
        sourceId: scriptNode.id,
        targetId: destId,
        weight: 1,
        categories: new Set<PayloadCategory>(cats),
        consentModes: new Set<ConsentMode>([r.consentMode]),
      });
    } else {
      edge.weight += 1;
      for (const c of cats) edge.categories.add(c);
      edge.consentModes.add(r.consentMode);
    }
  }

  const edges: DataSharingEdge[] = [];
  for (const e of edgeMap.values()) {
    const dominantCategory = dominantOf(e.categories);
    if (filters.sensitiveOnly) {
      if (!SENSITIVE_CATEGORIES.has(dominantCategory)) continue;
    }
    edges.push({
      sourceId: e.sourceId,
      targetId: e.targetId,
      weight: e.weight,
      dominantCategory,
      categories: e.categories,
      consentModes: e.consentModes,
    });
  }

  // Prune orphan nodes that were only referenced by filtered-out
  // edges so the side columns don't pile up nodes with no edges.
  const referencedSources = new Set(edges.map((e) => e.sourceId));
  const referencedTargets = new Set(edges.map((e) => e.targetId));

  const scriptList = Array.from(scripts.values())
    .filter((n) => referencedSources.has(n.id))
    .sort((a, b) => b.requestCount - a.requestCount || a.label.localeCompare(b.label))
    .map<DataSharingScriptNode>((n) => ({ id: n.id, scriptId: n.scriptId, label: n.label, requestCount: n.requestCount }));
  const destList = Array.from(destinations.values())
    .filter((n) => referencedTargets.has(n.id))
    .sort((a, b) => b.requestCount - a.requestCount || a.domain.localeCompare(b.domain));

  edges.sort((a, b) => b.weight - a.weight);

  return { scripts: scriptList, destinations: destList, edges };
}

// If the graph is too large for a clean Sankey rendering, fold the
// lightest-weight edges into an "other" bucket per-destination. 200 is
// a practical upper bound where Sankey's layout still reads cleanly at
// 960px wide.
export function collapseLowWeightEdges(graph: DataSharingGraph, maxEdges = 200): DataSharingGraph {
  if (graph.edges.length <= maxEdges) return graph;

  const sorted = [...graph.edges].sort((a, b) => b.weight - a.weight);
  const keep = sorted.slice(0, maxEdges - 1);
  const fold = sorted.slice(maxEdges - 1);

  if (fold.length === 0) return graph;

  const otherId = 'script:__other__';
  const otherNode: DataSharingScriptNode = {
    id: otherId,
    scriptId: null,
    label: `${fold.length} other scripts (collapsed)`,
    requestCount: fold.reduce((acc, e) => acc + e.weight, 0),
  };

  const perDest = new Map<
    string,
    { categories: Set<PayloadCategory>; modes: Set<ConsentMode>; weight: number }
  >();
  for (const e of fold) {
    const existing = perDest.get(e.targetId);
    if (existing) {
      existing.weight += e.weight;
      for (const c of e.categories) existing.categories.add(c);
      for (const m of e.consentModes) existing.modes.add(m);
    } else {
      perDest.set(e.targetId, {
        weight: e.weight,
        categories: new Set(e.categories),
        modes: new Set(e.consentModes),
      });
    }
  }

  const foldedEdges: DataSharingEdge[] = [];
  for (const [destId, acc] of perDest) {
    foldedEdges.push({
      sourceId: otherId,
      targetId: destId,
      weight: acc.weight,
      dominantCategory: dominantOf(acc.categories),
      categories: acc.categories,
      consentModes: acc.modes,
    });
  }

  return {
    scripts: [...graph.scripts, otherNode],
    destinations: graph.destinations,
    edges: [...keep, ...foldedEdges],
  };
}
